import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Paper,
  Stack,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import {
  CheckCircle,
  Close,
  MedicalInformation,
  RadioButtonUnchecked,
} from '@mui/icons-material';
import {
  Controller,
  FieldValues,
  PathValue,
  FieldPath,
  Control,
} from 'react-hook-form';
import { PatientHPOListsSchema } from '@/zod_schemas';

interface HPOSchema {
  hpo_id: string;
  name: string;
  gene: string[];
}

interface HPOChipSelectorProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  patientHpoList: PatientHPOListsSchema;
}

const HPOChipSelector = <T extends FieldValues>({
  control,
  name,
  patientHpoList,
}: HPOChipSelectorProps<T>) => {
  const allHpoTerms = patientHpoList.hpo;
  const allHpoIds = [...new Set([...patientHpoList.hpo.map((h) => h.hpo_id)])];

  const [viewDetails, setViewDetails] = useState<HPOSchema | null>(null);

  const handleClose = () => setViewDetails(null);

  return (
    <>
      <Controller
        name={name}
        control={control}
        defaultValue={allHpoIds as PathValue<T, FieldPath<T>>}
        render={({ field }) => {
          const selected: string[] = field.value ?? [];
          const { onChange } = field;

          const toggleHpo = (term: string) => {
            const updated = selected.includes(term)
              ? selected.filter((t) => t !== term)
              : [...selected, term];
            onChange(updated);
          };

          const areAllSelected = allHpoIds.every((id) => selected.includes(id));

          return (
            <FormControl fullWidth component="fieldset" sx={{ mb: 2 }}>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: 'background.default',
                  maxHeight: 350,
                  overflowY: 'auto',
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="body1" fontWeight={600}>
                    Terms
                  </Typography>
                  {allHpoTerms.length !== 0 && (
                    <Button
                      size="small"
                      variant="outlined"
                      hidden
                      onClick={() =>
                        onChange(areAllSelected ? [] : [...allHpoIds])
                      }
                    >
                      {areAllSelected ? 'Deselect All' : 'Select All'}
                    </Button>
                  )}
                </Stack>

                <Divider sx={{ mb: 2 }} />

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  {allHpoTerms.map((term) => {
                    const isSelected = selected.includes(term.hpo_id);

                    return (
                      <Tooltip
                        key={term.hpo_id}
                        title="Left-click to select, right-click to view details"
                        arrow
                      >
                        <Chip
                          label={term.hpo_id}
                          variant={isSelected ? 'filled' : 'outlined'}
                          color={isSelected ? 'primary' : 'default'}
                          onClick={() => toggleHpo(term.hpo_id)}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setViewDetails(term);
                          }}
                          icon={
                            isSelected ? (
                              <CheckCircle fontSize="small" />
                            ) : (
                              <RadioButtonUnchecked fontSize="small" />
                            )
                          }
                          sx={{
                            // border: isNew ? '1px dashed orange' : undefined,
                            broder: '1px dashed orange',
                            cursor: 'pointer',
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
              </Paper>
            </FormControl>
          );
        }}
      />

      {/* Detail dialog */}
      {viewDetails && (
        <Dialog
          open={Boolean(viewDetails)}
          onClose={handleClose}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 'bold',
              textAlign: 'center',
              fontSize: '1.2rem', // Reduces title font size
              py: 1.5, // Adjusts vertical padding (less height)
              position: 'relative',
            }}
          >
            <MedicalInformation sx={{ verticalAlign: 'middle', mr: 1 }} />
            <strong>Term Identifiers </strong>
            {viewDetails ? viewDetails.hpo_id : 'HPO Details'}
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'primary.contrastText',
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent
            sx={{
              bgcolor: 'background.default',
              p: 3,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {viewDetails && (
              <Paper
                elevation={3}
                sx={{
                  marginTop: 3,
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  width: '100%',
                  maxWidth: 500, // Prevents content from stretching too much
                  textAlign: 'center', // Centers text inside Paper
                }}
              >
                <Typography
                  variant="h6"
                  color="primary"
                  sx={{ fontWeight: 'bold' }}
                >
                  {viewDetails.name}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Stack spacing={2}>
                  <Typography>
                    <strong>Associate Genes:</strong>
                    {viewDetails.gene.map((gene) => gene).join(', ')}
                  </Typography>
                </Stack>
              </Paper>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default HPOChipSelector;
